// Alpine module for the Unique Listeners "Episode Breakdown" table on prefix analytics page
// Fetches paginated rows from the chart endpoint and wires pagination and date range controls.

import { wireDatePicker } from "../../../../shared/date_picker";
import { buildCsvString, triggerCsvDownload } from "../../shared/csv_download";

document.addEventListener('alpine:init', () => {
  // Shared store for pagination controls displayed outside the component scope
  try {
    if (!Alpine.store('uniqueListenersBreakdown')) {
      Alpine.store('uniqueListenersBreakdown', {
        page: 1,
        per_page: 10,
        total_pages: 1,
        total_count: 0,
        series: [],
        reload: null,
        next() { if (this.page < this.total_pages) { this.page += 1; if (typeof this.reload === 'function') this.reload(); } },
        prev() { if (this.page > 1) { this.page -= 1; if (typeof this.reload === 'function') this.reload(); } },
        set(p) {
          this.page = Number(p?.page) || 1;
          this.per_page = Number(p?.per_page) || 10;
          this.total_pages = Number(p?.total_pages) || 1;
          this.total_count = Number(p?.total_count) || 0;
          this.series = Array.isArray(p?.series) ? p.series : this.series;
        }
      });
    }
  } catch (_) {}

  Alpine.data('UniqueListenersBreakdownTable', () => ({
    rows: [],
    loading: false,
    apiUrl: null,
    pickerApi: null,

    init() {
      // Reuse the chart endpoint URL if present
      const chartEl = document.getElementById('UniqueListenersOverTimeChart');
      this.apiUrl = (chartEl?.dataset?.apiUrl) || '/analytics/chart';
      const store = Alpine.store('uniqueListenersBreakdown');
      store.reload = () => this.fetchPage();

      this.pickerApi = wireDatePicker({
        rootEl: document,
        onChange: () => { store.page = 1; this.fetchPage(); },
        inputSelector: '#unique-listeners-breakdown-range',
        buttonSelector: '[data-chart-type="unique-listeners-table"][data-chartid="uniqueListenersBreakdownTableID"][data-range]',
        hiddenFromSelector: '#unique_listeners_breakdown_from',
        hiddenToSelector: '#unique_listeners_breakdown_to',
        maxDate: 'today',
        // Match Episode Release Performance Comparison hue (light highlight)
        activeClasses: { add: ['bg-quill-100'], remove: ['bg-white'] }
      });

      // Wire CSV for Unique Listeners Episode Breakdown
      const dlBtn = document.getElementById('prefix-unique-breakdown-csv');
      if (dlBtn) {
        dlBtn.addEventListener('click', async () => {
          try {
            const activeBtn = document.querySelector('[data-chart-type="unique-listeners-table"][data-chartid="uniqueListenersBreakdownTableID"][data-range].bg-quill-100');
            const fromEl = document.getElementById('unique_listeners_breakdown_from');
            const toEl = document.getElementById('unique_listeners_breakdown_to');
            const from = (fromEl && fromEl.value) || '';
            const to = (toEl && toEl.value) || '';
            const baseParams = new URLSearchParams();
            baseParams.append('chart_type', 'listener_breakdown');
            if (activeBtn && activeBtn.dataset && activeBtn.dataset.range) {
              const val = String(activeBtn.dataset.range || '').toLowerCase();
              if (val === 'all_time' || val === 'all-time' || val === 'alltime') {
                baseParams.append('date_range', 'all_time');
              } else {
                const days = val.replace(/\D/g, '');
                if (days) baseParams.append('date_range', `${days}d`);
              }
            } else if (from && to) {
              baseParams.append('date_range', 'custom');
              baseParams.append('start_date', from);
              baseParams.append('end_date', to);
            } else {
              baseParams.append('date_range', 'all_time');
            }

            let page = 1;
            const perPage = 100;
            let totalPages = 1;
            const allRows = [];
            do {
              const params = new URLSearchParams(baseParams.toString());
              params.append('page', String(page));
              params.append('per_page', String(perPage));
              const res = await fetch(`${this.apiUrl}?${params.toString()}`, { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
              if (!res.ok) throw new Error(`unique breakdown csv fetch failed ${res.status}`);
              const data = await res.json();
              const rows = data.download_data || data.chart_data || [];
              rows.forEach(r => {
                allRows.push([
                  r.episode_number,
                  r.episode_name || r.name,
                  r.release_date,
                  r.total_unique_listeners || r.total || 0
                ]);
              });
              const p = data.pagination || { total_pages: page };
              totalPages = Number(p.total_pages || 1);
              page += 1;
            } while (page <= totalPages);

            const headers = ['Episode #', 'Episode Name', 'Release Date', 'Total Unique Listeners'];
            const csv = buildCsvString(headers, allRows);
            triggerCsvDownload(csv, 'unique_listeners_episode_breakdown.csv');
          } catch (e) { console.error('unique breakdown csv error', e); }
        });
      }

      // Clear button wiring for Unique Listeners Episode Breakdown
      try {
        const input = document.getElementById('unique-listeners-breakdown-range');
        const clearBtn = document.getElementById('unique-listeners-breakdown-clear');
        const fromEl = document.getElementById('unique_listeners_breakdown_from');
        const toEl = document.getElementById('unique_listeners_breakdown_to');
        const storeRef = Alpine.store('uniqueListenersBreakdown');

        const syncClearVisibility = () => {
          try {
            if (!clearBtn || !input) return;
            const show = Boolean(input.value && input.value.trim().length > 0);
            if (show) { clearBtn.classList.remove('hidden'); } else { clearBtn.classList.add('hidden'); }
          } catch (_) {}
        };
        const handleClear = () => {
          try {
            if (input) input.value = '';
            if (fromEl) fromEl.value = '';
            if (toEl) toEl.value = '';
            if (storeRef && typeof storeRef.reload === 'function') storeRef.reload();
            syncClearVisibility();
          } catch (_) {}
        };

        if (input) {
          input.addEventListener('input', syncClearVisibility);
          input.addEventListener('change', syncClearVisibility);
          setTimeout(syncClearVisibility, 0);
        }
        if (clearBtn) clearBtn.addEventListener('click', handleClear);
      } catch (_) {}
    },

    async load() { await this.fetchPage(); },

    async fetchPage() {
      this.loading = true;
      try {
        const store = Alpine.store('uniqueListenersBreakdown');
        const params = new URLSearchParams();
        params.append('chart_type', 'listener_breakdown');

        const activeTableBtn = document.querySelector('[data-chart-type="unique-listeners-table"][data-chartid="uniqueListenersBreakdownTableID"][data-range].bg-quill-100');
        const tableFromEl = document.getElementById('unique_listeners_breakdown_from');
        const tableToEl = document.getElementById('unique_listeners_breakdown_to');
        const from = (tableFromEl && tableFromEl.value) || '';
        const to = (tableToEl && tableToEl.value) || '';

        if (activeTableBtn && activeTableBtn.dataset && activeTableBtn.dataset.range) {
          const val = String(activeTableBtn.dataset.range || '').toLowerCase();
          if (val === 'all_time' || val === 'all-time' || val === 'alltime') {
            params.append('date_range', 'all_time');
          } else {
            const days = val.replace(/\D/g, '');
            if (days) params.append('date_range', `${days}d`);
          }
        } else if (from && to) {
          params.append('date_range', 'custom');
          params.append('start_date', from);
          params.append('end_date', to);
        } else {
          params.append('date_range', 'all_time');
        }
        params.append('page', String(store.page));
        params.append('per_page', String(store.per_page));

        const res = await fetch(`${this.apiUrl}?${params.toString()}`, { headers: { 'Accept': 'application/json' }, cache: 'no-store' });
        if (!res.ok) throw new Error(`listener_breakdown fetch failed ${res.status}`);
        const data = await res.json();

        const rows = data.chart_data || data.download_data || [];
        const p = data.pagination || { page: 1, per_page: 10, total_count: rows.length, total_pages: 1 };

        this.rows = rows.map(r => ({
          id: r.id,
          episode_number: r.episode_number,
          episode_name: r.episode_name || r.name,
          release_date: r.release_date,
          total_unique_listeners: r.total_unique_listeners || r.total || 0
        }));
        store.set(p);
        try {
          document.dispatchEvent(new CustomEvent('prefix:uniqueListenersBreakdown:pagination', {
            detail: { page: store.page, total_pages: store.total_pages }
          }));
        } catch (_) {}
      } catch (e) {
        console.error(e);
        this.rows = [];
        Alpine.store('uniqueListenersBreakdown').set({ page: 1, per_page: 10, total_pages: 1, total_count: 0 });
      } finally {
        this.loading = false;
      }
    }
  }));
});

 
